import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import useBorisChatApi from '../services/useBorisChatApi';
import useEscalation from '../hooks/useEscalation';
import useConversationalUserData from '../hooks/useConversationalUserData';
import EscalationForm from './EscalationForm';
import EscalationSuccess from './EscalationSuccess';
import UserDetailsForm from './UserDetailsForm';
import './BorisChatWidget.css';

import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

const BorisChatWidget = ({
  userId = null,
  autoStart = false, // Changed default to false
  onSessionCreated = null,
  onMessageSent = null,
  onAnalytics = null,
  requireUserDetails = true, // New prop to control whether user details are required
  useConversationalCollection = true // New prop to enable conversational data collection (vs form)
}) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [showStarterOptions, setShowStarterOptions] = useState(true);
  const [showUserDetailsForm, setShowUserDetailsForm] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(userId);
  const messagesEndRef = useRef(null);
  const hasShownInitialQuestionRef = useRef(false);
  const isProcessingResponseRef = useRef(false);

  const {
    sessionId,
    userName,
    isLoading,
    error,
    isConnected,
    createUser,
    createSession,
    sendMessage,
    clearError
  } = useBorisChatApi(false, currentUserId); // Always start with autoStart false

  // Escalation state management
  const {
    isModalOpen: isEscalationModalOpen,
    isSuccessModalOpen: isEscalationSuccessModalOpen,
    escalationSchema,
    successTicket,
    isSubmitting: isEscalationSubmitting,
    errors: escalationErrors,
    showEscalationForm,
    closeModal: closeEscalationModal,
    submitEscalation: handleEscalationSubmit,
    closeSuccessModal: closeEscalationSuccessModal,
  } = useEscalation();

  // Conversational user data collection
  const {
    isCollecting,
    isComplete: isUserDataCollectionComplete,
    collectionState,
    startCollection,
    processResponse,
    getCurrentQuestion,
    getCollectedData,
  } = useConversationalUserData();

  // Starter message options
  const starterOptions = [
    {
      id: 'pm-software',
      text: "Find the best Boris plan for my team",
      icon: ' 🧱'
    },
    {
      id: 'management-tools',
      text: "See pricing & purchase online",
      icon: ' 💸'
    },
    {
      id: 'choose-product',
      text: "I want help choosing the right solution",
      icon: '💬'
    }
  ];

  // Define callback functions first
  const addMessage = useCallback((content, type, uiComponents = null) => {
    const message = {
      id: Date.now() + Math.random(),
      type,
      content,
      timestamp: new Date(),
      uiComponents
    };
    setMessages(prev => [...prev, message]);
    return message;
  }, []);

  const handleSendMessage = useCallback(async (message, uiEvent = null) => {
    if (!sessionId) {
      console.error('No session available');
      return;
    }

    console.log('📤 Sending message:', message, 'uiEvent:', uiEvent);

    // Add user message if it's a text message
    if (message) {
      addMessage(message, 'user');
      setInputValue('');
      setShowStarterOptions(false); // Hide starter options after first message
    }

    // Sanitize uiEvent to prevent potential backend issues
    let sanitizedUIEvent = null;
    if (uiEvent) {
      try {
        // For option events, keep the structure simple (backend expects specific format)
        if (uiEvent.type === 'option') {
          if (uiEvent.ids) {
            // Multi-select batch
            sanitizedUIEvent = {
              type: 'option',
              ids: uiEvent.ids
            };
          } else if (uiEvent.id) {
            // Single select
            sanitizedUIEvent = {
              type: 'option',
              id: uiEvent.id
            };
          }
        } else {
          // For other events, preserve the structure
          sanitizedUIEvent = {
            type: uiEvent.type,
            ...(uiEvent.id && { id: uiEvent.id }),
            ...(uiEvent.action && { action: uiEvent.action }),
            ...(uiEvent.args && {
              args: typeof uiEvent.args === 'object' ?
                JSON.parse(JSON.stringify(uiEvent.args)) :
                uiEvent.args
            })
          };
        }
        console.log('🧹 Sanitized UI event:', sanitizedUIEvent);
      } catch (error) {
        console.error('Failed to sanitize UI event:', error);
        sanitizedUIEvent = { type: uiEvent.type || 'unknown' };
      }
    }

    try {
      const response = await sendMessage(message, sanitizedUIEvent);
      console.log('📥 Received response:', response);
      
      // Check for escalation signal
      if (response.signals && response.signals.escalation_required) {
        console.log('🚨 Escalation required, showing form');
        showEscalationForm(response.signals.escalation_schema, sessionId);
      }
      
      // Add bot response (handle both success and error responses)
      if (response.answer) {
        addMessage(response.answer, 'bot', response.ui);
      } else {
        // Handle backend validation errors gracefully
        addMessage('I received your input. Let me help you with that.', 'bot');
      }
      
      // Trigger callbacks
      if (onMessageSent) {
        onMessageSent(message, response);
      }
      
    } catch (error) {
      console.error('Failed to send message:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        uiEvent: sanitizedUIEvent
      });
      
      // More specific error messages based on error type
      if (error.response?.status === 500) {
        const errorDetail = error.response?.data?.detail || 'Unknown server error';
        console.error('🔴 Backend Error Details:', errorDetail);
        addMessage(`Server error detected: ${errorDetail}. Please inform the development team about this issue.`, 'bot');
      } else if (error.response?.status === 400) {
        addMessage('Sorry, there was an issue with that request. Please try again.', 'bot');
      } else if (!error.response) {
        addMessage('Sorry, I cannot connect to the server. Please check your connection and try again.', 'bot');
      } else {
        addMessage('Sorry, I encountered an error. Please try again.', 'bot');
      }
    }
  }, [sessionId, sendMessage, addMessage, onMessageSent, showEscalationForm]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Callback when session is created
  useEffect(() => {
    if (sessionId && onSessionCreated) {
      onSessionCreated(sessionId);
    }
  }, [sessionId, onSessionCreated]);

  // Effect to handle session creation and pending starter options
  useEffect(() => {
    if (sessionId) {
      // Check if there's a pending starter option to send
      const pendingOption = sessionStorage.getItem('pendingStarterOption');
      if (pendingOption) {
        try {
          const option = JSON.parse(pendingOption);
          sessionStorage.removeItem('pendingStarterOption');
          setTimeout(() => {
            handleSendMessage(option.text);
          }, 100);
        } catch (error) {
          console.error('Failed to parse pending starter option:', error);
        }
      }
    }
  }, [sessionId, handleSendMessage]);

  // Show question helper
  const showQuestion = useCallback((question) => {
    console.log('💬 [Chat] Showing question:', question.field, 'type:', question.type);
    if (question.type === 'text') {
      addMessage(question.text, 'bot');
    } else if (question.type === 'options') {
      addMessage(question.text, 'bot', {
        option_groups: [{
          title: 'Select an option',
          options: question.options.map(opt => ({
            id: opt.id,
            label: opt.label,
            kind: 'default'
          })),
          allow_multiple: false
        }]
      });
    }
  }, [addMessage]);

  // Effect to show the first question when conversational collection starts
  useEffect(() => {
    console.log('🎯 [Chat Effect] isCollecting:', isCollecting, 'isComplete:', isUserDataCollectionComplete, 'hasShown:', hasShownInitialQuestionRef.current, 'messages:', messages.length);

    if (isCollecting && !isUserDataCollectionComplete && !hasShownInitialQuestionRef.current && messages.length === 0) {
      // Only show the first question once
      console.log('🎬 [Chat Effect] Showing initial question');
      hasShownInitialQuestionRef.current = true;
      const question = getCurrentQuestion();
      if (question) {
        setTimeout(() => {
          showQuestion(question);
        }, 300);
      }
    }

    // Reset the ref when collection is complete
    if (isUserDataCollectionComplete) {
      console.log('✅ [Chat Effect] Collection complete, resetting ref');
      hasShownInitialQuestionRef.current = false;
    }
  }, [isCollecting, isUserDataCollectionComplete, messages.length, getCurrentQuestion, showQuestion]);

  // Effect to handle user data collection completion
  useEffect(() => {
    if (isUserDataCollectionComplete && !sessionId) {
      const collectedData = getCollectedData();
      if (collectedData) {
        // Create user with collected data, then create session
        const createUserAndSessionFromConversation = async () => {
          try {
            console.log('🎉 [Chat] Data collection complete, creating user silently...');

            // Step 1: Create user (silently, no message)
            const createdUser = await createUser(collectedData);
            console.log('✅ User created from conversation:', createdUser);

            // Update the userId
            const newUserId = createdUser.id || createdUser.user_id;
            setCurrentUserId(newUserId);
            setUserDetails({
              name: collectedData.name,
              email: collectedData.email,
              telephone: collectedData.telephone,
              acceptsPromotionalEmails: collectedData.accepts_promotional_emails
            });

            // Step 2: Create session with the new user ID (silently)
            setTimeout(async () => {
              try {
                console.log('🎉 [Chat] Creating session silently...');
                await createSession(newUserId);
                console.log('✅ Session created with user:', newUserId);

                // Clear the data collection messages and show starter options
                setTimeout(() => {
                  console.log('🎉 [Chat] Clearing messages and showing starter options');
                  setMessages([]); // Clear all messages
                  setShowStarterOptions(true); // Enable starter options
                }, 300);
              } catch (error) {
                console.error('❌ Failed to create session:', error);
                addMessage(
                  'Sorry, there was an issue starting the chat session. Please refresh the page.',
                  'bot'
                );
              }
            }, 500);
          } catch (error) {
            console.error('❌ Failed to create user from conversation:', error);
            addMessage(
              'Sorry, there was an issue saving your details. Please try again or contact support.',
              'bot'
            );
          }
        };
        createUserAndSessionFromConversation();
      }
    }
  }, [isUserDataCollectionComplete, getCollectedData, createUser, sessionId, addMessage, createSession]);

  // Handle user details form submission
  const handleUserDetailsSubmit = useCallback(async (formData) => {
    try {
      console.log('📋 Submitting user details:', formData);
      
      // Create user in the database
      const createdUser = await createUser({
        name: formData.name,
        email: formData.email,
        telephone: formData.telephone,
        accepts_promotional_emails: formData.acceptsPromotionalEmails,
        source: 'boris_chat_widget',
        metadata: {
          widget_version: '1.0',
          entry_point: 'chat_widget',
          timestamp: new Date().toISOString()
        }
      });

      console.log('✅ User created:', createdUser);
      
      // Store user details and ID
      setUserDetails(formData);
      setCurrentUserId(createdUser.id || createdUser.user_id);
      setShowUserDetailsForm(false);

      // Create session with the new user ID
      setTimeout(async () => {
        try {
          await createSession(createdUser.id || createdUser.user_id);
        } catch (error) {
          console.error('Failed to create session after user creation:', error);
        }
      }, 100);

    } catch (error) {
      console.error('❌ Failed to create user:', error);
      throw error; // This will be handled by the form component
    }
  }, [createUser, createSession]);

  // Handle starting the conversation (show user form, start conversational collection, or create session directly)
  const handleStartConversation = useCallback(() => {
    console.log('🚀 [Chat] Starting conversation');
    console.log('🚀 [Chat] requireUserDetails:', requireUserDetails, 'userDetails:', userDetails, 'currentUserId:', currentUserId);
    console.log('🚀 [Chat] useConversationalCollection:', useConversationalCollection);

    if (requireUserDetails && !userDetails && !currentUserId) {
      if (useConversationalCollection) {
        // Start conversational data collection WITHOUT creating session yet
        // Session will be created after all data is collected
        console.log('🚀 [Chat] Starting conversational collection');
        startCollection(); // Start the conversational collection process
        // The first question will be shown by the effect
      } else {
        // Show the traditional form
        console.log('🚀 [Chat] Showing traditional form');
        setShowUserDetailsForm(true);
      }
    } else {
      // If user details are not required or already provided, create session directly
      console.log('🚀 [Chat] Creating session directly with userId:', currentUserId);
      createSession(currentUserId);
    }
  }, [requireUserDetails, userDetails, currentUserId, createSession, useConversationalCollection, startCollection]);

  // Handle starter option clicks
  const handleStarterOptionClick = useCallback(async (option) => {
    if (!sessionId) {
      // If no session, start the conversation flow first
      if (requireUserDetails && !userDetails && !currentUserId) {
        setShowUserDetailsForm(true);
        // Store the selected option to send after user details are collected
        sessionStorage.setItem('pendingStarterOption', JSON.stringify(option));
        return;
      }

      // Create session first if it doesn't exist
      try {
        await createSession(currentUserId);
        // The session creation will trigger the effect above
        // We'll send the message in a timeout to ensure session is ready
        setTimeout(() => {
          handleSendMessage(option.text);
        }, 100);
      } catch (error) {
        console.error('Failed to create session for starter option:', error);
      }
    } else {
      await handleSendMessage(option.text);
    }
  }, [sessionId, createSession, currentUserId, handleSendMessage, requireUserDetails, userDetails]);

  // Handle conversational data collection responses
  const handleConversationalResponse = useCallback(async (response) => {
    console.log('📥 [Chat] Handling conversational response:', response);

    // Prevent concurrent processing
    if (isProcessingResponseRef.current) {
      console.log('⚠️ [Chat] Already processing, skipping...');
      return;
    }
    isProcessingResponseRef.current = true;
    console.log('🔒 [Chat] Processing locked');

    // Add user's message to chat
    addMessage(response, 'user');
    setInputValue('');

    // Wait a bit before processing to ensure smooth UX
    console.log('⏳ [Chat] Waiting 400ms before processing...');
    await new Promise(resolve => setTimeout(resolve, 400));

    // Process the response through the collection state machine
    console.log('🔄 [Chat] Processing response through state machine...');
    const result = processResponse(response);
    console.log('🔄 [Chat] Process result:', result);

    if (result.success) {
      if (result.completed) {
        // Collection is complete, user creation will be handled by useEffect
        console.log('🎉 [Chat] Collection completed!');
        isProcessingResponseRef.current = false;
        return;
      }

      // Move to next question - use the nextState and updatedUserData from result to get correct question
      console.log('⏳ [Chat] Waiting 500ms for smooth UX...');
      await new Promise(resolve => setTimeout(resolve, 500));
      console.log('❓ [Chat] Getting next question for state:', result.nextState, 'with userData:', result.updatedUserData);
      const nextQuestion = getCurrentQuestion(result.nextState, result.updatedUserData);
      if (nextQuestion) {
        console.log('➡️ [Chat] Moving to next question:', nextQuestion.field);
        showQuestion(nextQuestion);
      } else {
        console.log('⚠️ [Chat] No next question found!');
      }
    } else {
      // Validation failed, show error
      console.log('❌ [Chat] Validation failed, showing error');
      addMessage(result.error, 'bot');
    }

    console.log('🔓 [Chat] Processing unlocked');
    isProcessingResponseRef.current = false;
  }, [processResponse, getCurrentQuestion, addMessage, showQuestion]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (inputValue.trim()) {
      console.log('📝 [Chat] Form submitted with:', inputValue.trim());
      console.log('📝 [Chat] isCollecting:', isCollecting, 'isComplete:', isUserDataCollectionComplete);

      // If we're collecting user data conversationally, intercept and process as data collection response
      if (isCollecting && !isUserDataCollectionComplete) {
        console.log('📝 [Chat] Routing to conversational response handler');
        handleConversationalResponse(inputValue.trim());
      } else {
        console.log('📝 [Chat] Routing to normal message handler');
        handleSendMessage(inputValue.trim());
      }
    }
  }, [inputValue, handleSendMessage, isCollecting, isUserDataCollectionComplete, handleConversationalResponse]);

  const handleChipClick = useCallback(async (chip) => {
    console.log('🔷 Chip clicked:', chip);
    const uiEvent = {
      type: 'chip',
      id: chip.id,
      action: 'select',
      args: chip
    };
    console.log('🔷 Sending chip event:', uiEvent);
    await handleSendMessage(null, uiEvent);
  }, [handleSendMessage]);

  const handleCTAClick = useCallback(async (cta) => {
    console.log('🔶 CTA clicked:', cta);

    // Define CTA action to URL mappings
    const ctaUrlMap = {
      'compare_plans': 'https://boris-software.com/pricing/',
      'compare plans': 'https://boris-software.com/pricing/',
      'contact_sales': 'https://boris-software.com/contact-us/',
      'contact sales': 'https://boris-software.com/contact-us/',
      'buy_lite': 'https://boris-software.com/pricing/',
      'buy_pro': 'https://boris-software.com/pricing/',
      'buy_enterprise': 'https://boris-software.com/pricing/',
      'view_pricing': 'https://boris-software.com/pricing/',
    };

    // Check if CTA label matches any redirect patterns
    const labelLower = cta.label?.toLowerCase();
    const actionLower = cta.action?.toLowerCase();

    // Handle external URL actions directly in frontend
    if (cta.action === 'open_url' && cta.args?.url) {
      console.log('🔗 Opening external URL:', cta.args.url);
      window.open(cta.args.url, '_blank', 'noopener,noreferrer');
      return;
    }

    // Check if action matches a known URL mapping
    if (ctaUrlMap[actionLower]) {
      console.log('🔗 Opening mapped URL for action:', cta.action, '→', ctaUrlMap[actionLower]);
      window.open(ctaUrlMap[actionLower], '_blank', 'noopener,noreferrer');
      return;
    }

    // Check if label contains keywords that should redirect
    if (labelLower?.includes('compare') || labelLower?.includes('pricing')) {
      console.log('🔗 Opening pricing page based on label:', cta.label);
      window.open('https://boris-software.com/pricing/', '_blank', 'noopener,noreferrer');
      return;
    }

    if (labelLower?.includes('contact') || labelLower?.includes('sales')) {
      console.log('🔗 Opening contact page based on label:', cta.label);
      window.open('https://boris-software.com/contact-us/', '_blank', 'noopener,noreferrer');
      return;
    }

    if (labelLower?.includes('buy')) {
      console.log('🔗 Opening pricing page for purchase:', cta.label);
      window.open('https://boris-software.com/pricing/', '_blank', 'noopener,noreferrer');
      return;
    }

    // Send other CTA actions to backend
    const uiEvent = {
      type: 'cta',
      action: cta.action,
      args: cta.args
    };
    console.log('🔶 Sending CTA event to backend:', uiEvent);
    await handleSendMessage(null, uiEvent);
  }, [handleSendMessage]);

  const handleOptionSelect = useCallback(async (option, groupTitle) => {
    console.log('🔸 Option selected:', option, 'Group:', groupTitle);

    // If we're in conversational collection mode, handle as collection response
    if (isCollecting && !isUserDataCollectionComplete) {
      const currentQuestion = getCurrentQuestion();
      if (currentQuestion && currentQuestion.type === 'options') {
        // Find the selected option value
        const selectedOption = currentQuestion.options.find(opt => opt.id === option.id);
        if (selectedOption) {
          // Prevent concurrent processing
          if (isProcessingResponseRef.current) {
            return;
          }
          isProcessingResponseRef.current = true;

          // Add user's choice to chat
          addMessage(selectedOption.label, 'user');

          // Wait a bit before processing
          await new Promise(resolve => setTimeout(resolve, 400));

          // Process the boolean value
          const result = processResponse(selectedOption.value);

          if (result.success && result.completed) {
            // Collection is complete
            isProcessingResponseRef.current = false;
            return;
          }

          isProcessingResponseRef.current = false;
        }
      }
      return;
    }

    // Normal option selection handling (not in collection mode)
    // Handle multi-select (batch selection with ids array)
    if (option.ids && Array.isArray(option.ids)) {
      const uiEvent = {
        type: 'option',
        ids: option.ids
      };
      console.log('🔸 Sending batch option event:', uiEvent);
      await handleSendMessage(null, uiEvent);
    } else {
      // Handle single selection (backward compatible)
      const uiEvent = {
        type: 'option',
        id: option.id
      };
      console.log('🔸 Sending single option event:', uiEvent);
      await handleSendMessage(null, uiEvent);
    }
  }, [handleSendMessage, isCollecting, isUserDataCollectionComplete, getCurrentQuestion, addMessage, processResponse]);

  const handleQuickReply = useCallback(async (question) => {
    console.log('⚡ Quick reply clicked:', question);
    const uiEvent = {
      type: 'quick_reply',
      args: { intent: question.intent }
    };
    console.log('⚡ Sending quick reply event:', uiEvent);
    await handleSendMessage(question.text, uiEvent);
  }, [handleSendMessage]);

  // Handle escalation form submission
  const handleEscalationFormSubmit = useCallback(async (formData) => {
    try {
      console.log('📋 Submitting escalation form:', formData);
      const success = await handleEscalationSubmit(formData);
      
      if (success) {
        // Add a message to the chat indicating escalation was submitted
        addMessage(
          `✅ Your support request has been submitted successfully! Your ticket will be processed by our team soon.`, 
          'bot'
        );
      }
      
      return success;
    } catch (error) {
      console.error('❌ Failed to submit escalation:', error);
      return false;
    }
  }, [handleEscalationSubmit, addMessage]);

  if (!isConnected) {
    return (
      <div className="boris-chat-widget error">
        <div className="connection-error">
          <h3>Connection Error</h3>
          <p>Unable to connect to Boris Sales Bot API</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="boris-chat-widget">
      <div className="chat-header">
        <div className="header-content">
          <h3>Boris Sales Assistant</h3>
        </div>
      </div>

      <div className="chat-messages">
        {/* Welcome message - shown before session creation and data collection */}
        {!sessionId && !isCollecting && messages.length === 0 && (
          <div className="message bot">
            <div className="message-content">
              <span style={{ fontSize: '1.5em', fontWeight: '600' }}>
                👋 Hi!
              </span>
              <br /><br />
              {`I'm Boris, your digital product advisor.`}
              <br /><br />
              We help construction, compliance, and service teams manage projects, fieldwork, and compliance in one simple platform.
              <br /><br />
              Let's find the right solution for your team.
            </div>
            <div style={{ marginTop: '1rem' }}>
              <button
                onClick={handleStartConversation}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
                disabled={isLoading}
              >
                🚀 Start Conversation
              </button>
            </div>
          </div>
        )}

        {/* Starter options - shown after session is created and we have userName, and NOT collecting data */}
        {sessionId && userName && showStarterOptions && !isCollecting && (
          <div className="message bot">
            <div className="message-content">
              <span style={{ fontSize: '1.5em', fontWeight: '600' }}>
                {`👋 Hi, `}
                <strong>{userName}</strong>
                !
              </span>
              <br /><br />
              {`I'm Boris, your digital product advisor.`}
              <br /><br />
              We help construction, compliance, and service teams manage projects, fieldwork, and compliance in one simple platform.
              <br /><br />
              Let's find the right solution for your team — where would you like to start?
            </div>

            <div className="starter-options">
              {starterOptions.map((option) => (
                <button
                  key={option.id}
                  className="starter-option-btn"
                  onClick={() => handleStarterOptionClick(option)}
                  disabled={isLoading}
                >
                  <span className="option-icon">{option.icon}</span>
                  <span className="option-text">{option.text}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        
        {messages.map((message) => (
          <div key={message.id} className={`message ${message.type}`}>
            <div className="message-content">
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                >
                  {message.content}
                </ReactMarkdown>
            </div>
            
            {/* Render UI Components */}
            {message.uiComponents && (
              <UIComponentRenderer 
                components={message.uiComponents}
                onChipClick={handleChipClick}
                onCTAClick={handleCTAClick}
                onOptionSelect={handleOptionSelect}
                onQuickReply={handleQuickReply}
              />
            )}
            
            <div className="message-timestamp">
              {message.timestamp.toLocaleTimeString()}
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="message bot loading">
            <div className="typing-indicator">
              <span></span><span></span><span></span>
            </div>
            <div className="loading-text">Boris is thinking...</div>
          </div>
        )}

        {error && (
          <div className="message error">
            <div className="error-content">
              <span>❌ {error}</span>
              <button onClick={clearError} className="clear-error">×</button>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      
      <form onSubmit={handleSubmit} className="chat-input-form">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={
            !sessionId && !isCollecting
              ? "Click 'Start Conversation' above to begin"
              : isCollecting
              ? "Type your answer..."
              : "Type your message..."
          }
          disabled={isLoading || (!sessionId && !isCollecting)}
          className="chat-input"
        />
        <button
          type="submit"
          disabled={isLoading || !inputValue.trim() || (!sessionId && !isCollecting)}
          className="send-button"
        >
          {isLoading ? '⏳' : '📤'}
        </button>
      </form>

      {/* Escalation Form Modal */}
      {isEscalationModalOpen && escalationSchema && (
        <EscalationForm
          schema={escalationSchema}
          sessionId={sessionId}
          onSubmit={handleEscalationFormSubmit}
          onCancel={closeEscalationModal}
          isSubmitting={isEscalationSubmitting}
          errors={escalationErrors}
        />
      )}

      {/* Escalation Success Modal */}
      {isEscalationSuccessModalOpen && successTicket && (
        <EscalationSuccess
          ticket={successTicket}
          onClose={closeEscalationSuccessModal}
        />
      )}

      {/* User Details Form Modal */}
      {showUserDetailsForm && (
        <UserDetailsForm
          onSubmit={handleUserDetailsSubmit}
          isLoading={isLoading}
          onCancel={() => setShowUserDetailsForm(false)}
        />
      )}
    </div>
  );
};

// Option Groups Renderer with Multi-Select Support
const OptionGroupsRenderer = ({ optionGroups, onOptionSelect }) => {
  const [selectedOptions, setSelectedOptions] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleOptionClick = (option, groupTitle, groupIdx, allowMultiple, maxSelections = 10) => {
    const groupKey = `group_${groupIdx}`;

    if (allowMultiple) {
      // Multi-select: toggle option
      setSelectedOptions(prev => {
        const currentSelections = prev[groupKey] || [];
        const isSelected = currentSelections.some(opt => opt.id === option.id);

        if (isSelected) {
          // Remove option
          return {
            ...prev,
            [groupKey]: currentSelections.filter(opt => opt.id !== option.id)
          };
        } else {
          // Add option (respect max limit)
          if (currentSelections.length >= maxSelections) {
            return prev; // Max selections reached
          }
          return {
            ...prev,
            [groupKey]: [...currentSelections, option]
          };
        }
      });
    } else {
      // Single select: submit immediately
      onOptionSelect(option, groupTitle);
    }
  };

  const handleContinue = async (groupTitle, groupIdx, minSelections = 1) => {
    const groupKey = `group_${groupIdx}`;
    const selections = selectedOptions[groupKey] || [];

    if (selections.length < minSelections) {
      alert(`Please select at least ${minSelections} option${minSelections > 1 ? 's' : ''}`);
      return;
    }

    setIsSubmitting(true);

    // Send selections - always use ids array format for multi-select
    await onOptionSelect({
      ids: selections.map(s => s.id),
      labels: selections.map(s => s.label).join(', ')
    }, groupTitle);

    setIsSubmitting(false);
  };

  return (
    <div className="option-groups">
      {optionGroups.map((group, groupIdx) => {
        const groupKey = `group_${groupIdx}`;
        const currentSelections = selectedOptions[groupKey] || [];

        // Check if this group allows multiple selections (default to false for single-select)
        const allowMultiple = group.allow_multiple === true;
        const minSelections = group.min_selections || 1;
        const maxSelections = group.max_selections || 10;

        return (
          <div key={groupIdx} className="option-group">
            {allowMultiple && (
              <p style={{ fontSize: '0.9em', color: '#666', marginTop: '-8px' }}>
                Select one or more options
              </p>
            )}

            <div className="options">
              {group.options.map((option) => {
                const isSelected = currentSelections.some(opt => opt.id === option.id);
                return (
                  <button
                    key={option.id}
                    className={`option ${option.kind || ''} ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleOptionClick(option, group.title, groupIdx, allowMultiple, maxSelections)}
                    disabled={isSubmitting}
                  >
                    <span className="option-label">{option.label}</span>
                    {option.description && (
                      <span className="option-description">{option.description}</span>
                    )}
                  </button>
                );
              })}
            </div>

            {allowMultiple && currentSelections.length >= minSelections && (
              <button
                className="continue-button"
                onClick={() => handleContinue(group.title, groupIdx, minSelections)}
                disabled={isSubmitting}
                style={{
                  marginTop: '1rem',
                  padding: '0.75rem 2rem',
                  background: '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  width: '100%'
                }}
              >
                {isSubmitting ? 'Submitting...' : `Continue (${currentSelections.length} selected)`}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

// UI Components Renderer
const UIComponentRenderer = ({
  components,
  onChipClick,
  onCTAClick,
  onOptionSelect,
  onQuickReply
}) => {
  if (!components) return null;

  // Hide chips if option_groups are present (preference for option groups)
  const shouldShowChips = components.chips && components.chips.length > 0 &&
                          (!components.option_groups || components.option_groups.length === 0);

  return (
    <div className="ui-components">
      {/* Render Chips - Hidden when option_groups present */}
      {shouldShowChips && (
        <div className="chips-container">
          {components.chips.map((chip) => (
            <button
              key={chip.id}
              className={`chip ${chip.kind} ${chip.selected ? 'selected' : ''}`}
              onClick={() => onChipClick(chip)}
              disabled={chip.disabled}
            >
              {chip.label}
            </button>
          ))}
        </div>
      )}

      {/* Render Product Cards */}
      {components.product_cards && components.product_cards.length > 0 && (
        <div className="cards-container">
          {components.product_cards.map((card) => (
            <div key={card.product_id} className="product-card">
              <div className="card-header">
                <h4>{card.title}</h4>
                {card.badge && <span className="badge">{card.badge}</span>}
              </div>
              
              {card.subtitle && <p className="subtitle">{card.subtitle}</p>}
              
              <div className="fit-score">
                <span>Match Score: {Math.round(card.fit_score * 100)}%</span>
                <div className="score-bar">
                  <div 
                    className="score-fill" 
                    style={{ width: `${card.fit_score * 100}%` }}
                  ></div>
                </div>
              </div>
              
              {card.highlights.length > 0 && (
                <ul className="highlights">
                  {card.highlights.map((highlight, idx) => (
                    <li key={idx}>{highlight}</li>
                  ))}
                </ul>
              )}
              
              {card.evidence.length > 0 && (
                <div className="evidence">
                  <strong>Why recommended:</strong>
                  <ul>
                    {card.evidence.map((reason, idx) => (
                      <li key={idx}>{reason}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {card.pricing_summary && (
                <div className="pricing">{card.pricing_summary}</div>
              )}
              
              {card.ctas.length > 0 && (
                <div className="card-actions">
                  {card.ctas.map((cta, idx) => (
                    <button
                      key={idx}
                      className={`cta ${cta.primary ? 'primary' : 'secondary'}`}
                      onClick={() => onCTAClick(cta)}
                      disabled={cta.disabled}
                    >
                      {cta.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Render Option Groups */}
      {components.option_groups && components.option_groups.length > 0 && (
        <OptionGroupsRenderer
          optionGroups={components.option_groups}
          onOptionSelect={onOptionSelect}
        />
      )}

      {/* Render Next Questions */}
      {components.next_questions && components.next_questions.length > 0 && (
        <div className="next-questions">
          {components.next_questions.map((question, idx) => (
            <button
              key={idx}
              className="next-question"
              onClick={() => onQuickReply(question)}
            >
              {question.text}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default BorisChatWidget;